// externals
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// libraries
import type { DashMiddleware, DashNext, DashStore } from '@makemydeal/dr-shared-store';
import { bootstrapActionTypes, initActionTypes } from '@makemydeal/dr-shared-store';
import type { DealerReviewBaggageContent } from '@makemydeal/dr-shared-types';
import type { PendingReviewContext, PendingReviewResolutionPath, StateTree } from '@makemydeal/dr-dash-types';

// actions
import * as offerActionCreators from '../actions/offerActionCreators';
import * as dealHistoryActionCreators from '../actions/dealHistoryActionCreator';

// consts/enums
import { FETCH_DEAL_HISTORY_SUCCESS } from '../actionTypes/dealHistoryActionTypes';
import { PAYMENT_FAILURE, PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import type { FetchDealerReviewBaggageSuccessAction } from '../actionTypes/dealHistoryActionTypes';
import {
    FETCH_DEALER_REVIEW_BAGGAGE_FAILURE,
    FETCH_DEALER_REVIEW_BAGGAGE_SUCCESS,
    MAKE_CURRENT_COMPLETED,
    RESOLVE_PENDING_DEALER_REVIEW_PAYMENT,
    RESOLVE_PENDING_REVIEW_REQUESTED
} from '../actionTypes/offerActionTypes';
import { SCENARIOS_FETCH_FAILURE } from '../actionTypes/multiScenarioActionTypes';
import { SOCKET_EVENT_RECEIVED } from '../actionTypes/socketActionTypes';

// selectors
import * as dealHistorySelectors from '../selectors/dealHistorySelectors';
import * as dealValidationSelectors from '../selectors/dealValidationSelectors';
import * as vehicleSelectors from '../selectors/vehicleSelectors';
import * as offerInfoSelectors from '../selectors/offerInfoSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import {
    isPendingDealerReview,
    isPendingDealerReviewFromSocket,
    isSocketVersionPaymentSettled,
    isSocketVersionPendingReview
} from '../selectors/pendingDealerReviewSelectors';

// utils
import { readUsableDealerReviewMarker } from '../utils/dealerReviewBaggageUtil';
import { willRefetchHistoryOnSocketEvent } from './dealHistoryMiddleware';
import { isPaymentMiddlewareReady } from './paymentMiddleware';

const { INIT_PENCIL_SUCCESS, INIT_SUCCESS } = initActionTypes;

export const VEHICLE_OF_INTEREST_FIELD = 'vehicleOfInterest';

// No vehicle-identity cross-check when a usable marker exists: if Sales Check-In reports a vehicle
// change the dealer decides, even if the vehicle turns out to be equivalent.
export const markerReportsVehicleChange = (marker: DealerReviewBaggageContent): boolean =>
    marker.changedFields.includes(VEHICLE_OF_INTEREST_FIELD);

const sellingVehicleIdentity = (vehicle: { vin?: string; stockNumber?: string } | undefined): string | undefined => {
    const vin = vehicle?.vin?.trim();
    if (vin) return `vin:${vin.toLowerCase()}`;
    const stockNumber = vehicle?.stockNumber?.trim();
    return stockNumber ? `stock:${stockNumber.toLowerCase()}` : undefined;
};

// Fallback for a missing marker, which is ordinary rather than exceptional: it is a best-effort patch
// written after the version save, into one deal-level slot the next publish overwrites. Undefined means
// "cannot tell yet", so the caller defers rather than defaulting to recalculation — which would silently
// apply a vehicle switch carrying the old vehicle's incentives and taxes.
export const compareSellingVehicles = (state: StateTree): PendingReviewResolutionPath | undefined => {
    // getLatestDealerOffer, not offers[1] — see getPreChangeVehicleInfo for why.
    const currentIdentity = sellingVehicleIdentity(vehicleSelectors.getVehicle(state));
    const previousIdentity = sellingVehicleIdentity(dealHistorySelectors.getLatestDealerOffer(state)?.vehicle);

    if (!currentIdentity || !previousIdentity) {
        return undefined;
    }

    return currentIdentity === previousIdentity ? 'recalculate' : 'itemRetention';
};

// Settled on the later of marker-available and history-available; undefined until then, so the
// resolution action cannot run against a branch that was never decided.
export const resolveBranch = (
    state: StateTree,
    marker: DealerReviewBaggageContent | undefined
): PendingReviewResolutionPath | undefined => {
    if (marker) {
        return markerReportsVehicleChange(marker) ? 'itemRetention' : 'recalculate';
    }

    return compareSellingVehicles(state);
};

export const PENDING_REVIEW_EVENTS = {
    DETECTED: 'MV.PendingDealerReview.Detected',
    PATH_SELECTED: 'MV.PendingDealerReview.PathSelected',
    RESOLVED: 'MV.PendingDealerReview.Resolved',
    PRICED_WITHOUT_RESOLUTION: 'MV.PendingDealerReview.PricedWithoutResolution',
    BAGGAGE_UNAVAILABLE: 'MV.PendingDealerReview.BaggageUnavailable'
} as const;

// Applying a pending change discards every non-primary scenario, and applying is the only route out of
// a pending status, so the frequency is measured rather than assumed.
const getAlternateScenarioCount = (state: StateTree): number => {
    // getScopesOrder is a bare `state.__scopeMap.order` read; telemetry must never break a resolution.
    if (!(state as any).__scopeMap) {
        return 0;
    }

    const order = scopedStateSelectors.getScopesOrder(state as any);
    return Array.isArray(order) ? Math.max(order.length - 1, 0) : 0;
};

const logPendingReviewEvent = (state: StateTree, eventName: string, attributes: Record<string, unknown>): void => {
    logNewRelicPageAction(eventName, {
        dealXgId: offerReduxSelectors.getDealXgId(state),
        alternateScenarioCount: getAlternateScenarioCount(state),
        ...attributes
    });
};

// Sales Check-In announces the version it saved and patches the marker naming it, so a marker naming
// anything else describes another change — an earlier one, or this one before the patch landed.
export const isMarkerForAnnouncedVersion = (marker: DealerReviewBaggageContent | undefined, announcedVersion: string): boolean => {
    if (!marker) {
        return false;
    }

    // Nothing announced means there is no version to disagree with, so the marker stands.
    return !announcedVersion || marker.dealXgVersion === announcedVersion;
};

const buildContext = (
    state: StateTree,
    marker: DealerReviewBaggageContent | undefined,
    targetVersion: string
): PendingReviewContext => ({
    targetDealXgVersion: targetVersion,
    marker,
    changedFields: marker?.changedFields ?? [],
    resolutionPath: resolveBranch(state, marker),
    isResolving: false,
    trigger: 'load'
});

// Not `BOOTSTRAP_SUCCESS` / `INIT_SUCCESS`, though they carry the same baggage: the predicate also reads
// `featureToggles` and `calculationDetails`, written only on `INIT_PENCIL_SUCCESS`, so detecting earlier
// sees an unset toggle and silently never fires. `FETCH_DEAL_HISTORY_SUCCESS` carries no baggage, so it
// may only complete a context — creating one there would clobber a good marker with nothing.
const CONTEXT_CREATING_ACTIONS: string[] = [INIT_PENCIL_SUCCESS];

// Set when a recalculation was wanted before the payment engine was wired up: the load path gets here
// while BOOTSTRAP_SUCCESS is still propagating — sharedBootstrapMiddleware nested-dispatches
// INIT_PENCIL_SUCCESS before calling next() — so a payment dispatched now is silently dropped.
let deferredRecalculation = false;

export const pendingDealerReviewMiddlewareClearCache = (): void => {
    deferredRecalculation = false;
};

// Mirrors quoteModeInitialPaymentMiddleware, which owns the isPaymentMiddlewareReady contract.
const requestRecalculation = (store: DashStore): void => {
    if (!isPaymentMiddlewareReady()) {
        deferredRecalculation = true;
        return;
    }
    deferredRecalculation = false;
    store.dispatch(offerActionCreators.resolvePendingDealerReviewPayment());
};

// Only the recalculation path dispatches anything: the decision screen opens off `isResolving` plus an
// `itemRetention` path, so there is no third flag to keep in sync.
const proceedWithResolution = (store: DashStore, context: PendingReviewContext): void => {
    if (context.resolutionPath === 'recalculate') {
        requestRecalculation(store);
    }
};

// Not hosted in the banner component: it can be mounted twice concurrently, so a once-per-version side
// effect there would fire twice, and the branch decision needs deal history, which arrives later.
export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    next(action);

    if (action.type === PAYMENT_RECEIVED || action.type === PAYMENT_FAILURE) {
        // Keep the direct `originalAction?.type === CONSTANT` shape: paymentSequenceExemptList.spec.ts
        // AST-scans for it, and hoisting the left side into a local makes the exemption read as
        // unjustified and fails that gate.
        if (action.meta?.originalAction?.type === RESOLVE_PENDING_DEALER_REVIEW_PAYMENT) {
            // A failed payment still clears the pending status — the dealer did review. Failed
            // self-heals on the next successful calculation.
            const outcome = action.type === PAYMENT_RECEIVED ? 'resolved' : 'resolvedWithFailure';
            logPendingReviewEvent(store.getState() as StateTree, PENDING_REVIEW_EVENTS.RESOLVED, { outcome });
            store.dispatch(offerActionCreators.setPendingReviewResolution(outcome));
            return;
        }

        if (action.type === PAYMENT_RECEIVED) {
            const state = store.getState() as StateTree;
            if (isPendingDealerReview(state) && offerInfoSelectors.getPendingReviewResolution(state) === undefined) {
                logPendingReviewEvent(state, PENDING_REVIEW_EVENTS.PRICED_WITHOUT_RESOLUTION, {
                    trigger: action.meta?.originalAction?.type ?? 'unknown'
                });
            }
        }
        return;
    }

    if (action.type === SOCKET_EVENT_RECEIVED) {
        const state = store.getState() as StateTree;
        const announcedVersion = offerInfoSelectors.getSocketEventDealVersion(state);

        const existingContext = offerInfoSelectors.getPendingReview(state);
        const isResolved = offerInfoSelectors.getPendingReviewResolution(state) !== undefined;
        const movesTheTarget = Boolean(announcedVersion) && announcedVersion !== existingContext?.targetDealXgVersion;

        // `isResolved` only turns true once the payment lands, so it does not cover an apply still in
        // flight. Retargeting during one makes MAKE_CURRENT_COMPLETED below see the applied version
        // disagree with the target and clear the review, so the dealer's click ends with no
        // recalculation and no banner.
        if (
            existingContext &&
            !existingContext.isResolving &&
            !isResolved &&
            movesTheTarget &&
            !isSocketVersionPaymentSettled(state)
        ) {
            store.dispatch(offerActionCreators.setPendingReviewTargetVersion(announcedVersion));
        }

        if (!isPendingDealerReviewFromSocket(state)) {
            return;
        }

        if (isSocketVersionPaymentSettled(state)) {
            return;
        }

        // dealHistoryMiddleware bails on an invalid deal, so neither fetch below has an owner there.
        if (!dealValidationSelectors.isDealValid(state)) {
            return;
        }

        // The baggage read is always required: the notification carries none.
        store.dispatch(dealHistoryActionCreators.fetchDealerReviewBaggage(state, announcedVersion));

        // Only forced when dealHistoryMiddleware will not fetch it itself — dispatching regardless
        // issued the same request twice in parallel.
        if (!willRefetchHistoryOnSocketEvent(state)) {
            store.dispatch(dealHistoryActionCreators.fetchDealHistory(state));
        }
        return;
    }

    // The endpoint ships separately, so a 404 is the shape of "deployed out of order" — without this the
    // socket path stops with nothing saying why.
    if (action.type === FETCH_DEALER_REVIEW_BAGGAGE_FAILURE) {
        const state = store.getState() as StateTree;
        if (isPendingDealerReviewFromSocket(state)) {
            logPendingReviewEvent(state, PENDING_REVIEW_EVENTS.BAGGAGE_UNAVAILABLE, {
                announcedVersion: offerInfoSelectors.getSocketEventDealVersion(state) || ''
            });
        }
        return;
    }

    if (action.type === FETCH_DEALER_REVIEW_BAGGAGE_SUCCESS) {
        const state = store.getState() as StateTree;
        const baggageRead = action as FetchDealerReviewBaggageSuccessAction;
        const readMarker = readUsableDealerReviewMarker(baggageRead.payload?.baggage);
        const announcedVersion = offerInfoSelectors.getSocketEventDealVersion(state);
        const readForAnnouncedVersion = baggageRead.meta?.announcedVersion ?? '';
        const marker = isMarkerForAnnouncedVersion(readMarker, readForAnnouncedVersion) ? readMarker : undefined;

        if (!marker && !isPendingDealerReview(state) && !isSocketVersionPendingReview(state)) {
            return;
        }

        if (!marker && offerInfoSelectors.getPendingReview(state)?.marker) {
            return;
        }

        const targetVersion = announcedVersion || offerInfoSelectors.getDealXgVersion(state) || '';
        const context: PendingReviewContext = {
            ...buildContext(state, marker, targetVersion),
            trigger: 'socket'
        };

        // Sales Check-In reuses the version identifier across publishes, so `changedAt` is what moves
        // and a fresher marker overrides the latch. "No context" falls back to the latch, because the
        // double-prompt case is a re-bootstrap, where the context and its marker are both still there.
        const alreadyHandled = offerInfoSelectors.getPendingReviewHandledVersion(state) === context.targetDealXgVersion;
        const existingContext = offerInfoSelectors.getPendingReview(state);
        const existingMarker = existingContext?.marker;
        const isSameChangeAsContext =
            marker !== undefined && existingMarker !== undefined && marker.changedAt === existingMarker.changedAt;
        const isChangeNotYetSeen = marker !== undefined && existingContext !== undefined && !isSameChangeAsContext;
        const alreadyResolved = offerInfoSelectors.getPendingReviewResolution(state) !== undefined;

        if (isSameChangeAsContext || ((alreadyHandled || alreadyResolved) && !isChangeNotYetSeen)) {
            return;
        }

        logPendingReviewEvent(state, PENDING_REVIEW_EVENTS.DETECTED, {
            trigger: 'socket',
            hasMarker: Boolean(context.marker),
            changedFieldCount: context.changedFields.length,
            resolutionPath: context.resolutionPath ?? 'undecided'
        });
        store.dispatch(offerActionCreators.setPendingReviewContext(context));
        store.dispatch(offerActionCreators.markPendingReviewHandled(context.targetDealXgVersion));
        return;
    }

    if (action.type === RESOLVE_PENDING_REVIEW_REQUESTED) {
        const state = store.getState() as StateTree;
        const context = offerInfoSelectors.getPendingReview(state);
        if (!context) {
            return;
        }

        logPendingReviewEvent(state, PENDING_REVIEW_EVENTS.PATH_SELECTED, {
            trigger: context.trigger,
            resolutionPath: context.resolutionPath ?? 'undecided'
        });

        // On load the marker's version IS the loaded version, so re-running the apply would clone the
        // scope and re-bootstrap for no state change.
        if (context.trigger === 'load') {
            proceedWithResolution(store, context);
            return;
        }

        // Targets the marker's version, never the socket's.
        store.dispatch(dealHistoryActionCreators.makeCurrentDeal(context.targetDealXgVersion || undefined));
        return;
    }

    // Both actions are tried because their relative order is not guaranteed; the latch keeps it idempotent.
    if (action.type === INIT_SUCCESS || action.type === bootstrapActionTypes.BOOTSTRAP_SUCCESS) {
        if (deferredRecalculation) {
            // The context can be abandoned between defer and drain, and recalculating for a resolution
            // nothing is waiting on would price a deal the dealer never asked about.
            const stillResolving = offerInfoSelectors.getPendingReview(store.getState() as StateTree)?.isResolving;
            if (!stillResolving) {
                deferredRecalculation = false;
                return;
            }
            requestRecalculation(store);
        }
        return;
    }

    if (action.type === MAKE_CURRENT_COMPLETED) {
        const state = store.getState() as StateTree;
        const context = offerInfoSelectors.getPendingReview(state);
        if (!context || !context.isResolving) {
            return;
        }

        // See clearPendingReview for why a mismatch is abandoned rather than resolved.
        const appliedVersion = offerReduxSelectors.getDealXgVersion(state);
        if (context.targetDealXgVersion && appliedVersion && appliedVersion !== context.targetDealXgVersion) {
            store.dispatch(offerActionCreators.clearPendingReview());
            return;
        }

        proceedWithResolution(store, context);
        return;
    }

    // Abandon rather than leaving a context stuck mid-resolution with no terminal.
    if (action.type === SCENARIOS_FETCH_FAILURE) {
        const context = offerInfoSelectors.getPendingReview(store.getState() as StateTree);
        if (context?.isResolving) {
            store.dispatch(offerActionCreators.clearPendingReview());
        }
        return;
    }

    const isCreating = CONTEXT_CREATING_ACTIONS.includes(action.type);
    const isCompleting = action.type === FETCH_DEAL_HISTORY_SUCCESS;

    if (!isCreating && !isCompleting) {
        return;
    }

    const state = store.getState() as StateTree;

    if (!isPendingDealerReview(state)) {
        return;
    }

    const existing = offerInfoSelectors.getPendingReview(state);

    if (isCompleting) {
        if (existing && existing.resolutionPath === undefined) {
            const resolutionPath = compareSellingVehicles(state);
            if (resolutionPath !== undefined) {
                store.dispatch(offerActionCreators.setPendingReviewResolutionPath(resolutionPath));

                // The load path was waiting on this comparison; resume it rather than leaving the deal
                // silently unresolved.
                if (existing.trigger === 'load' && !existing.isResolving) {
                    store.dispatch(offerActionCreators.resolvePendingReviewRequested());
                }
            }
        }
        return;
    }

    const marker = readUsableDealerReviewMarker(action.payload?.baggage);
    // From the payload for the same reason as the author: `offerInfo.dealXgVersion` is written on
    // BOOTSTRAP_SUCCESS, which is forwarded only after this action, so state still holds nothing here.
    const loadedVersion = action.payload?.offer?.dealExchangeVersionId ?? '';
    const context = buildContext(state, marker, loadedVersion);

    if (offerInfoSelectors.getPendingReviewHandledVersion(state) === context.targetDealXgVersion) {
        return;
    }

    logPendingReviewEvent(state, PENDING_REVIEW_EVENTS.DETECTED, {
        trigger: 'load',
        hasMarker: Boolean(marker),
        changedFieldCount: context.changedFields.length,
        resolutionPath: context.resolutionPath ?? 'undecided'
    });
    store.dispatch(offerActionCreators.setPendingReviewContext(context));
    store.dispatch(offerActionCreators.markPendingReviewHandled(context.targetDealXgVersion));

    // Automatic on load — no banner, no dealer click. Safe here and not on a live update: the pending
    // version is already the loaded one, so nothing is applied and no dealer work is discarded.
    if (context.resolutionPath !== undefined) {
        store.dispatch(offerActionCreators.resolvePendingReviewRequested());
    }
};
